import {
  Box,
  Button,
  Flex,
  Img,
  Link as ChakraLink,
  Stack,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { Link } from 'react-scroll';
import {
  Activity as TActivity,
  ActivityFragmentFragment,
  TermCategory,
} from '../../../generated/types';
import Container from '../../Layout/Container';
import PageHeader from '../../Layout/PageHeader';
import PageImages, { SliderImage } from '../../Common/PageImages';
import SidebarWrapper from '../../Layout/SidebarWrapper';
import ActivityTeaser from './ActivityTeaser';
import PoiTeaser from '../Poi/PoiTeaser';
import HotelTeaser from '../Hotel/HotelTeaser';
import CategoryBadge from '../../Common/CategoryBadge';
import useIsMobile from '../../../hooks/useIsMobile';
import React from 'react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import ContentTitle from '../../Common/ContentTitle';
import ContentBlock from '../../Common/ContentBlock';
import { secondsToHumanString } from '../../../utils/helper';
import ActivityCategoryBadge from '../../Common/ActivityCategoryBadge';
import TeaserSlider from '../../Common/TeaserSlider';
import { useStore } from '../../../store/provider';
import getIconColorForCategoryKey from '../../../utils/getIconColorForCategoryKey';
import { entityFilterConditionToQueryString } from '../../../utils/parseFilterQueryString';
import Wrapper from '../Wrapper';
import useActivityHotelActivitiesForCategory from '../../../hooks/useActivityHotelActivitiesForCategory';
import useActivityHotelMarkers from '../../../hooks/useActivityHotelMarkers';

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

interface ActivityProps {
  data: ActivityFragmentFragment;
}

const Activity: React.FC<ActivityProps> = ({ data }) => {
  const isMobile = useIsMobile();
  const store = useStore();

  const hotelActivitiesForCategory = useActivityHotelActivitiesForCategory(
    data
  );
  const mapMarkers = useActivityHotelMarkers(data);

  return (
    <Wrapper typename={data.__typename}>
      <Box>
        <PageHeader
          breadcrumb={[
            {
              href: '/',
              label: 'Startseite',
            },
            {
              href: `/entdecken/${data.category.frontendUrl}`,
              label: `${data.category.labelPlural}`,
            },
            {
              href: `/entdecken/${
                data.category.frontendUrl
              }?filter=${encodeURIComponent(
                entityFilterConditionToQueryString({
                  field: 'country_tid',
                  operator: '=',
                  value: data.country?.id + '',
                })
              )}`,
              label: data.country?.name,
            },
            {
              href: `/entdecken/${
                data.category.frontendUrl
              }?filter=${encodeURIComponent(
                entityFilterConditionToQueryString({
                  field: 'region_tid',
                  operator: '=',
                  value: data.region?.id + '',
                })
              )}`,
              label: data.region?.name,
            },
            {
              href: `${data.url}`,
              label: data.title,
              isCurrentPage: true,
            },
          ]}
          preTitle={
            <ActivityCategoryBadge category={data.category as TermCategory} />
          }
          title={data.title}
          subTitle={data.region?.name}
          ActionComponent={
            data.gpx ? (
              <a target="_blank" download href={`${data.gpx?.url}`}>
                <Button size="sm" as="span" colorScheme="blue">
                  GPX-Download
                </Button>
              </a>
            ) : null
          }
        />

        <Container paddingY={4}>
          <Flex
            flexDirection={{
              base: 'column',
              md: 'column',
              lg: 'row',
            }}
          >
            <Box overflow="hidden" flex="1" p="3" pl="0">
              <PageImages
                images={[
                  data.teaserImage as SliderImage,
                  ...((data.images as SliderImage[]) || []),
                ]}
              />

              <Box marginY={8} alignSelf="flex-start">
                <Stack isInline={!isMobile} spacing={4} justifyContent="center">
                  {data.difficulty && (
                    <CategoryBadge
                      label="Schwierigkeit"
                      value={data.difficulty.name}
                    />
                  )}
                  {data.duration && (
                    <CategoryBadge
                      label="Dauer"
                      value={secondsToHumanString(data.duration)}
                    />
                  )}
                  {data.length && (
                    <CategoryBadge label="Länge" value={`${data.length}km`} />
                  )}
                </Stack>
              </Box>

              {data.body && (
                <ContentBlock title="Beschreibung">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data.body,
                    }}
                  />
                </ContentBlock>
              )}
            </Box>

            {data.hotels && data.hotels.total > 0 && (
              <SidebarWrapper>
                <ContentTitle small title="Empfohlenes Hotel" />
                {data.hotels?.total > 0 && (
                  <Box marginTop={4}>
                    {data.hotels!.items!.map(
                      (hotel) =>
                        hotel && <HotelTeaser key={hotel.id} data={hotel} />
                    )}
                  </Box>
                )}

                <Stack marginTop={2}>
                  {hotelActivitiesForCategory.category.length > 0 && (
                    <Button
                      cursor="pointer"
                      as={Link}
                      to="hotelActivitiesForCategory_category"
                      color={
                        getIconColorForCategoryKey(data.category!.key!) ||
                        'brand'
                      }
                      smooth
                      variant="outline"
                    >
                      Weitere {data.category!.labelPlural} des Hotels
                      <ArrowForwardIcon marginLeft={2} />
                    </Button>
                  )}

                  {hotelActivitiesForCategory.other.length > 0 &&
                    store.config.renderConfig.activity.hotelActivitiesOther && (
                      <Button
                        cursor="pointer"
                        to="hotelActivitiesForCategory_other"
                        smooth
                        as={Link}
                        variant="outline"
                      >
                        Weitere Aktivitäten des Hotels
                        <ArrowForwardIcon marginLeft={2} />
                      </Button>
                    )}
                </Stack>

                {store.config.renderConfig.activity.renderSidebarAdTeaser && (
                  <>
                    <ChakraLink href="https://www.bikeleasing.de" isExternal>
                      <Img
                        mx="auto"
                        mt={2}
                        src="/bike-leasing.gif"
                        boxSize="367"
                      />
                    </ChakraLink>
                  </>
                )}
              </SidebarWrapper>
            )}
          </Flex>
        </Container>
        {/* {data.hotels && data.hotels.total > 0 && (
                <Container smallest paddingY={16}>
                    <Heading textAlign="center" size="lg" marginBottom={8}>
                        Empfohlenes Hotel
                    </Heading>
                    {data.hotels.items.map((hotel) => (
                        <HotelTeaser key={hotel.id} data={hotel} />
                    ))}
                </Container>
            )} */}

        <HotelMap
          scrollWheelZoomDelayed
          scrollWheelZoom={false}
          data={data}
          markers={mapMarkers}
          center={data.location}
          elevationData={data.elevationData || undefined}
        />

        {store.config.renderConfig.activity.hotelActivities &&
          hotelActivitiesForCategory.category.length > 0 && (
            <Container id="hotelActivitiesForCategory_category" paddingTop={8}>
              <ContentBlock noDivider>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle
                      title={`Weitere ${data.category!.labelPlural} des Hotels`}
                    />
                  }
                  CardComponent={ActivityTeaser}
                  data={hotelActivitiesForCategory.category}
                />
              </ContentBlock>
            </Container>
          )}

        {store.config.renderConfig.activity.hotelActivitiesOther &&
          hotelActivitiesForCategory.other.length > 0 && (
            <Container id="hotelActivitiesForCategory_other" paddingTop={8}>
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle title={`Weitere Aktivitäten des Hotels`} />
                  }
                  CardComponent={ActivityTeaser}
                  data={hotelActivitiesForCategory.other}
                />
              </ContentBlock>
            </Container>
          )}

        {store.config.renderConfig.activity.nearHotels &&
          (!data.hotels || data.hotels.total === 0) &&
          data.nearHotels &&
          data.nearHotels.total > 0 && (
            <Container paddingTop={8}>
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={<ContentTitle title={`Hotels in der Nähe`} />}
                  CardComponent={HotelTeaser}
                  data={data.nearHotels.items || []}
                />
              </ContentBlock>
            </Container>
          )}

        {store.config.renderConfig.activity.nearActivities &&
          data.nearActivities?.total! > 0 && (
            <Container paddingTop={8}>
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle title={`Aktivitäten in der Nähe`} />
                  }
                  CardComponent={ActivityTeaser}
                  data={data.nearActivities?.items || []}
                />
              </ContentBlock>
            </Container>
          )}

        {store.config.renderConfig.activity.nearPois &&
          data.nearPois?.total! > 0 && (
            <Container paddingTop={8}>
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle title={`Weitere Tipps in der Nähe`} />
                  }
                  CardComponent={PoiTeaser}
                  data={data.nearPois?.items || []}
                />
              </ContentBlock>
            </Container>
          )}
        <Box marginBottom={12} />
      </Box>
    </Wrapper>
  );
};

export default Activity;
